import { put, takeEvery, takeLatest, all, fork, select } from 'redux-saga/effects'
import { decamelizeKeys } from 'humps'
import * as actions from './actions'

import {
  API_URL,
  REPORTS_ENDPOINT
} from 'qap/constants'
import { requestActions } from 'state/request'

import { stringify } from 'utilities/apiQueryString'
import { getEndpointByDocType } from 'utilities/state'

import type { ReportUserStatus } from './types'
import { failure, success } from '../../utilities/actions'
import { getMinReportsToFetch } from './helpers'
import { extractFailureError } from './inlineUpdates'

export function* fetchReports(action: any): Generator<any, any, any> {
  const docType = action?.options?.docType
  const { filter, page, sort } = yield select(state => state.reports)

  const queryString = stringify({ filter, page, sort })
  const url = `${API_URL}/${getEndpointByDocType(docType)}?${queryString ? `&${queryString}` : ''}`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'GET',
    url,
    cancelKey: 'reports.list',
    successAction: actions.API_REPORTS_INDEX_SUCCESS,
    failureAction: actions.API_REPORTS_INDEX_FAILURE
  })
}

export function* cacheMoreReports(action: any): Generator<any, any, any> {
  const { payload: { options: { docType }, extendLimitBy = 0 } } = action
  const { filter, page, sort, cachedReports } = yield select(state => state.reports)
  const { offset = 0, limit = 0 } = page || {}

  const minReportsToFetch = getMinReportsToFetch(cachedReports.length, offset, limit)
  const updatedPage = { ...page, offset: 0, limit: minReportsToFetch + extendLimitBy } as const
  const queryString = stringify({ filter, page: updatedPage, sort })
  const url = `${API_URL}/${getEndpointByDocType(docType)}?${queryString ? `&${queryString}` : ''}`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'GET',
    url,
    cancelKey: 'reports.list',
    successAction: success(actions.CACHE_MORE_REPORTS),
    failureAction: failure(actions.CACHE_MORE_REPORTS)
  })
}

export function* fetchReportFacets(action: any): Generator<any, any, any> {
  const docType = action?.options?.docType
  const { filter } = yield select(state => state.reports)

  const queryString = stringify({ filter })
  const url = `${API_URL}/${getEndpointByDocType(docType)}/facets?${queryString ? `&${queryString}` : ''}`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'GET',
    url,
    cancelKey: 'reports.facets',
    successAction: actions.API_REPORTS_FACETS_INDEX_SUCCESS,
    failureAction: actions.API_REPORTS_FACETS_INDEX_FAILURE
  })
}

export function* fetchAllReportFacets(action: any): Generator<any, any, any> {
  const docType = action?.options?.docType
  const url = `${API_URL}/${getEndpointByDocType(docType)}/facets`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'GET',
    url,
    cancelKey: 'reports.allFacets',
    hideLoading: true,
    skipGlobalErrorHandling: true,
    successAction: actions.API_REPORTS_ALL_FACETS_INDEX_SUCCESS,
    failureAction: actions.API_REPORTS_ALL_FACETS_INDEX_FAILURE
  })
}

function* fetchReportDetail(action: any): Generator<any, any, any> {
  const url = `${API_URL}/${REPORTS_ENDPOINT}/${action.reportId}`

  yield put({
    type: requestActions.AUTHED_REQUEST,
    hideLoading: true,
    method: 'GET',
    url,
    successAction: actions.FETCH_REPORT_DETAIL_SUCCESS,
    failureAction: actions.FETCH_REPORT_DETAIL_FAILURE
  })
}

export function* updateUserStatus(
  action: {
    userStatus: ReportUserStatus;
  },
): Generator<any, any, any> {
  const { reportId, docType, ...userStatus } = action.userStatus
  const url = `${API_URL}/${getEndpointByDocType(docType)}/${reportId}/user_status`

  yield put(actions.beginInlineUpdate(reportId, 'userStatus'))

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'PUT',
    url,
    body: { data: decamelizeKeys({ ...userStatus }) },
    successAction: actions.UPDATE_REPORT_USER_STATUS_SUCCESS,
    failureAction: actions.UPDATE_REPORT_USER_STATUS_FAILURE,
    context: { reportId },
  })
}

export function* updateUserStatusInList(action: any): Generator<any, any, any> {
  const { id, userStatus } = action.data ?? {}
  if (!id) return

  yield put(actions.setSingleReportUserStatus(id, userStatus))
  yield put(actions.successInlineUpdate(id, 'userStatus'))
}

export function* failToUpdateUserStatus(action: any): Generator<any, any, any> {
  const reportId = action.context?.reportId
  if (!reportId) return
  yield put(actions.failureInlineUpdate(reportId, 'userStatus', extractFailureError(action)))
}

function* watchFetchReports(): Generator<any, any, any> {
  yield takeLatest([
    actions.API_REPORTS_FACETS_INDEX,
    actions.API_REPORTS_SET_ALL_FILTERS
  ], fetchReportFacets)

  yield takeLatest([
    actions.API_REPORTS_INDEX,
    actions.API_REPORTS_SET_ALL_FILTERS
  ], fetchReports)

  yield takeLatest([
    actions.CACHE_MORE_REPORTS
  ], cacheMoreReports)

  yield takeLatest([
    actions.FETCH_REPORT_DETAIL
  ], fetchReportDetail)

  yield takeLatest([
    actions.API_REPORTS_ALL_FACETS_INDEX
  ], fetchAllReportFacets)
}

function* watchUserStatusChange(): Generator<any, any, any> {
// @ts-expect-error - TS2769 - No overload matches this call.
  yield takeLatest(actions.UPDATE_REPORT_USER_STATUS, updateUserStatus)
  yield takeEvery(actions.UPDATE_REPORT_USER_STATUS_SUCCESS, updateUserStatusInList)
  yield takeEvery(actions.UPDATE_REPORT_USER_STATUS_FAILURE, failToUpdateUserStatus)
}

export default function* reportsSagas(): Generator<any, any, any> {
  yield all([
    fork(watchFetchReports),
    fork(watchUserStatusChange)
  ])
}
